import type React from 'react';
import type { CSSProperties } from 'react';

// PNG Icons
import AIFileIcon from '@/assets/icons/file-icons/AI_file_icon.png';
import AVIFileIcon from '@/assets/icons/file-icons/AVI_file_icon.png';
import BMPFileIcon from '@/assets/icons/file-icons/BMP_file_icon.png';
import CRDFileIcon from '@/assets/icons/file-icons/CRD_file_icon.png';
import CSVFileIcon from '@/assets/icons/file-icons/CSV_file_icon.png';
import DLLFileIcon from '@/assets/icons/file-icons/DLL_file_icon.png';
import DOCXFileIcon from '@/assets/icons/file-icons/DOCX_file_icon.png';
import DOCFileIcon from '@/assets/icons/file-icons/DOC_file_icon.png';
import DWGFileIcon from '@/assets/icons/file-icons/DWG_file_icon.png';
import EPSFileIcon from '@/assets/icons/file-icons/EPS_file_icon.png';
import EXEFileIcon from '@/assets/icons/file-icons/EXE_file_icon.png';
import FLVFileIcon from '@/assets/icons/file-icons/FLV_file_icon.png';
import FileFileIcon from '@/assets/icons/file-icons/File_file_icon.png';
import GIFFFileIcon from '@/assets/icons/file-icons/GIFF_file_icon.png';
import HTMLFileIcon from '@/assets/icons/file-icons/HTML_file_icon.png';
import ISOFileIcon from '@/assets/icons/file-icons/ISO_file_icon.png';
import JAVAFileIcon from '@/assets/icons/file-icons/JAVA_file_icon.png';
import JPGFileIcon from '@/assets/icons/file-icons/JPG_file_icon.png';
import MDBFileIcon from '@/assets/icons/file-icons/MDB_file_icon.png';
import MIDFileIcon from '@/assets/icons/file-icons/MID_file_icon.png';
import MOVFileIcon from '@/assets/icons/file-icons/MOV_file_icon.png';
import MP3FileIcon from '@/assets/icons/file-icons/MP3_file_icon.png';
import MP4FileIcon from '@/assets/icons/file-icons/MP4_file_icon.png';
import MPEGFileIcon from '@/assets/icons/file-icons/MPEG_file_icon.png';
import PDFFileIcon from '@/assets/icons/file-icons/PDF_file_icon.png';
import PNGFileIcon from '@/assets/icons/file-icons/PNG_file_icon.png';
import PPTFileIcon from '@/assets/icons/file-icons/PPT_file_icon.png';
import PSDFileIcon from '@/assets/icons/file-icons/PSD_file_icon.png';
import PSFileIcon from '@/assets/icons/file-icons/PS_file_icon.png';
import PUBFileIcon from '@/assets/icons/file-icons/PUB_file_icon.png';
import RARFileIcon from '@/assets/icons/file-icons/RAR_file_icon.png';
import RAWFileIcon from '@/assets/icons/file-icons/RAW_file_icon.png';
import RSSFileIcon from '@/assets/icons/file-icons/RSS_file_icon.png';
import SVGFileIcon from '@/assets/icons/file-icons/SVG_file_icon.png';
import TIFFFileIcon from '@/assets/icons/file-icons/TIFF_file_icon.png';
import TXTFileIcon from '@/assets/icons/file-icons/TXT_file_icon.png';
import WAVFileIcon from '@/assets/icons/file-icons/WAV_file_icon.png';
import WMAFileIcon from '@/assets/icons/file-icons/WMA_file_icon.png';
import XLSXFileIcon from '@/assets/icons/file-icons/XLSX_file_icon.png';
import XLSFileIcon from '@/assets/icons/file-icons/XLS_file_icon.png';
import XMLFileIcon from '@/assets/icons/file-icons/XML_file_icon.png';
import XSLFileIcon from '@/assets/icons/file-icons/XSL_file_icon.png';
import ZIPFileIcon from '@/assets/icons/file-icons/ZIP_file_icon.png';
// Note: XSLX_file_icon.png is likely a duplicate/typo for XLSX, so using XLSXFileIcon for xlsx

/**
 * Định dạng byte như văn bản có thể đọc được của con người.
 *
 * @param bytes Number of bytes.
 * @param si True to use metric (SI) units, aka powers of 1000. False to use
 *           binary (IEC), aka powers of 1024.
 * @param dp Number of decimal places to display.
 *
 * @return Chuỗi được định dạng.
 */
export function humanFileSize(bytes: number, si = false, dp = 1) {
  const thresh = si ? 1000 : 1024;

  if (Math.abs(bytes) < thresh) {
    return `${bytes} B`;
  }

  const units = si
    ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
  let u = -1;
  const r = 10 ** dp;

  do {
    bytes /= thresh;
    ++u;
  } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);

  return `${bytes.toFixed(dp)} ${units[u]}`;
}

/**
 * Maps a file extension to a corresponding PNG icon image with optional styling and click handler.
 * Supports a wide range of file types including documents, images, videos, audio, archives, and more.
 *
 * @param {string} extension - The file extension to map to an icon (e.g., 'pdf', 'jpg', 'mp4').
 * @param {CSSProperties} style - Optional inline styles for the img element.
 * @param {() => void} [onClick] - Optional click handler function for the icon.
 * @returns {React.ReactElement} The img element with the corresponding PNG icon.
 *
 * @example
 * ```tsx
 * const icon = mapFileExtensionToIcon('pdf', { width: 40, height: 40 }, () => console.log('Icon clicked'));
 * ```
 */
export const mapFileExtensionToIcon = (
  extension: string,
  style?: CSSProperties,
  onClick?: () => void
): React.ReactElement => {
  const defaultStyle: CSSProperties = {
    width: 24,
    height: 24,
    cursor: onClick ? 'pointer' : 'default',
    ...style
  };

  const iconMap: Record<string, string> = {
    // Documents
    pdf: PDFFileIcon,
    doc: DOCFileIcon,
    docx: DOCXFileIcon,
    xls: XLSFileIcon,
    xlsx: XLSXFileIcon,
    ppt: PPTFileIcon,
    pptx: PPTFileIcon,
    txt: TXTFileIcon,
    md: TXTFileIcon,
    rtf: DOCFileIcon, // fallback
    pub: PUBFileIcon,
    ps: PSFileIcon,
    eps: EPSFileIcon,
    mdb: MDBFileIcon,

    // Images
    jpg: JPGFileIcon,
    jpeg: JPGFileIcon,
    png: PNGFileIcon,
    gif: GIFFFileIcon,
    bmp: BMPFileIcon,
    svg: SVGFileIcon,
    tiff: TIFFFileIcon,
    tif: TIFFFileIcon,
    psd: PSDFileIcon,
    raw: RAWFileIcon,
    ai: AIFileIcon,

    // Archives
    zip: ZIPFileIcon,
    rar: RARFileIcon,
    '7z': ZIPFileIcon,
    tar: ZIPFileIcon,
    gz: ZIPFileIcon,
    iso: ISOFileIcon,

    // Videos
    mp4: MP4FileIcon,
    avi: AVIFileIcon,
    mov: MOVFileIcon,
    mpeg: MPEGFileIcon,
    mpg: MPEGFileIcon,
    flv: FLVFileIcon,

    // Audio
    mp3: MP3FileIcon,
    wav: WAVFileIcon,
    wma: WMAFileIcon,
    mid: MIDFileIcon,
    midi: MIDFileIcon,

    // Code/Web
    html: HTMLFileIcon,
    htm: HTMLFileIcon,
    xml: XMLFileIcon,
    xsl: XSLFileIcon,
    java: JAVAFileIcon,
    rss: RSSFileIcon,
    csv: CSVFileIcon,

    // Other
    exe: EXEFileIcon,
    dll: DLLFileIcon,
    dwg: DWGFileIcon,
    crd: CRDFileIcon
  };

  const src = iconMap[extension.toLowerCase()] || FileFileIcon;
  const alt = `${extension.toUpperCase()} file icon`;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLImageElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.();
    }
  };

  return (
    <img
      src={src}
      alt={alt}
      style={defaultStyle}
      onClick={onClick}
      onKeyDown={onClick ? handleKeyDown : undefined}
      tabIndex={onClick ? 0 : undefined}
      role={onClick ? 'button' : undefined}
    />
  );
};

/**
 * Generates a comma-separated string of accepted MIME types and file extensions.
 *
 * @param acceptObject - An object where keys are MIME types and values are arrays of file extensions.
 * @returns A string containing MIME types and file extensions separated by commas.
 */
export const generateAcceptString = (acceptObject: Record<string, string[]>) => {
  return Object.entries(acceptObject)
    .flatMap(([mimeType, extensions]) => [mimeType, ...extensions])
    .join(',');
};

/**
 * Truncates a file name to fit within the specified maximum length, preserving the file extension.
 * If the name is shorter than or equal to the max length, it is returned unchanged.
 * The truncation prioritizes showing parts of the name from the beginning and end, separated by '...'.
 * If there's insufficient space even for the extension, it returns '...' followed by the extension.
 *
 * @param name - The original file name to truncate.
 * @param maxLength - The maximum allowed length for the truncated name.
 * @returns The truncated file name, including the extension if present.
 */
export const truncateFileName = (name: string, maxLength: number): string => {
  if (name.length <= maxLength) {
    return name;
  }
  const extensionIndex = name.lastIndexOf('.');
  const hasExtension = extensionIndex !== -1;
  const extension = hasExtension ? name.substring(extensionIndex) : '';
  const nameWithoutExtension = hasExtension ? name.substring(0, extensionIndex) : name;

  const charsToShow = maxLength - extension.length - 3; // 3 for '...'
  if (charsToShow <= 0) {
    // Not enough space to show any part of the name, just show '...'
    return `...${extension}`;
  }

  const frontChars = Math.ceil(charsToShow / 2);
  const backChars = Math.floor(charsToShow / 2);

  const truncated = `${nameWithoutExtension.substring(0, frontChars)}...${nameWithoutExtension.substring(nameWithoutExtension.length - backChars)}`;

  return `${truncated}${extension}`;
};
